import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Mail, Phone, Globe, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

interface PartnerInquiry {
  id: string;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string | null;
  website_url: string | null;
  message: string;
  partnership_type: string;
  status: string;
  notes: string | null;
  created_at: string;
}

const AdminPartnerInquiries = () => {
  const [inquiries, setInquiries] = useState<PartnerInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState<PartnerInquiry | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const { data, error } = await supabase
        .from("partner_inquiries")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setInquiries(data || []);
    } catch (error) {
      console.error("Error fetching inquiries:", error);
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetail = (inquiry: PartnerInquiry) => {
    setSelectedInquiry(inquiry);
    setStatus(inquiry.status);
    setNotes(inquiry.notes || "");
    setShowDetailDialog(true);
  };

  const handleUpdateInquiry = async () => {
    if (!selectedInquiry) return;

    try {
      const { error } = await supabase
        .from("partner_inquiries")
        .update({
          status,
          notes,
        })
        .eq("id", selectedInquiry.id);

      if (error) throw error;
      toast.success("Inquiry updated successfully");
      setShowDetailDialog(false);
      fetchInquiries();
    } catch (error) {
      console.error("Error updating inquiry:", error);
      toast.error("Failed to update inquiry");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "new":
        return "bg-blue-500";
      case "contacted":
        return "bg-yellow-500";
      case "qualified":
        return "bg-green-500";
      case "rejected":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getPartnershipTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      "co-training": "Co-Training",
      "product": "Product",
      "service": "Service",
      "affiliate": "Affiliate",
      "sponsorship": "Sponsorship",
      "other": "Other",
    };
    return labels[type] || type;
  };

  const filterByStatus = (statusFilter: string) => {
    return inquiries.filter(i => i.status === statusFilter);
  };

  const newInquiries = filterByStatus("new");
  const contactedInquiries = filterByStatus("contacted");
  const qualifiedInquiries = filterByStatus("qualified");
  const rejectedInquiries = filterByStatus("rejected");

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin/partners">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-4xl font-bold">Partnership Inquiries</h1>
            <p className="text-muted-foreground mt-2">
              Manage incoming partnership requests
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                New Inquiries
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{newInquiries.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Contacted
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{contactedInquiries.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Qualified
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{qualifiedInquiries.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Inquiries
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{inquiries.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="new" className="space-y-6">
          <TabsList>
            <TabsTrigger value="new">New ({newInquiries.length})</TabsTrigger>
            <TabsTrigger value="contacted">Contacted ({contactedInquiries.length})</TabsTrigger>
            <TabsTrigger value="qualified">Qualified ({qualifiedInquiries.length})</TabsTrigger>
            <TabsTrigger value="rejected">Rejected ({rejectedInquiries.length})</TabsTrigger>
          </TabsList>

          {["new", "contacted", "qualified", "rejected"].map((statusTab) => {
            const filteredInquiries = filterByStatus(statusTab);
            return (
              <TabsContent key={statusTab} value={statusTab} className="space-y-4">
                {filteredInquiries.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      No {statusTab} inquiries
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredInquiries.map((inquiry) => (
                      <Card key={inquiry.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleOpenDetail(inquiry)}>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-lg">{inquiry.company_name}</CardTitle>
                              <CardDescription className="mt-1">
                                {inquiry.contact_name} • {getPartnershipTypeLabel(inquiry.partnership_type)}
                              </CardDescription>
                            </div>
                            <Badge className={getStatusColor(inquiry.status)}>{inquiry.status}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span>{inquiry.email}</span>
                          </div>
                          {inquiry.phone && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <span>{inquiry.phone}</span>
                            </div>
                          )}
                          {inquiry.website_url && (
                            <div className="flex items-center gap-2 text-sm">
                              <Globe className="h-4 w-4 text-muted-foreground" />
                              <a href={inquiry.website_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                                Visit Website
                              </a>
                            </div>
                          )}
                          <div className="pt-2">
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {inquiry.message}
                            </p>
                          </div>
                          <div className="text-xs text-muted-foreground pt-2">
                            {new Date(inquiry.created_at).toLocaleDateString()} at {new Date(inquiry.created_at).toLocaleTimeString()}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </div>

      {/* Detail Dialog */}
      {selectedInquiry && (
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedInquiry.company_name}</DialogTitle>
              <DialogDescription>Partnership inquiry details</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Contact Name</Label>
                  <p className="font-medium">{selectedInquiry.contact_name}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Partnership Type</Label>
                  <p className="font-medium">{getPartnershipTypeLabel(selectedInquiry.partnership_type)}</p>
                </div>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground">Email</Label>
                <a href={`mailto:${selectedInquiry.email}`} className="block text-primary hover:underline">
                  {selectedInquiry.email}
                </a>
              </div>

              {selectedInquiry.phone && (
                <div>
                  <Label className="text-sm text-muted-foreground">Phone</Label>
                  <a href={`tel:${selectedInquiry.phone}`} className="block text-primary hover:underline">
                    {selectedInquiry.phone}
                  </a>
                </div>
              )}

              {selectedInquiry.website_url && (
                <div>
                  <Label className="text-sm text-muted-foreground">Website</Label>
                  <a href={selectedInquiry.website_url} target="_blank" rel="noopener noreferrer" className="block text-primary hover:underline">
                    {selectedInquiry.website_url}
                  </a>
                </div>
              )}

              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Message</Label>
                <Card>
                  <CardContent className="pt-6">
                    <p className="whitespace-pre-wrap">{selectedInquiry.message}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Internal Notes</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add internal notes about this inquiry..."
                  rows={4}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateInquiry}>
                Update Inquiry
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdminPartnerInquiries;
